import { Flex, HStack, Heading } from '@chakra-ui/layout'
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  IconButton,
  PinInput,
  PinInputField,
  Button,
  Text,
  useDisclosure,
  VStack,
  Link
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import React, { useRef, useState } from 'react'
import { use100vh } from 'react-div-100vh'
import { useForm, Controller } from 'react-hook-form'
import { ActionModal, BaseHeading } from '.'
import { ErrorMessage } from './form'
import { Axios } from '../utils/axios'
import { ROUTES } from '../contants'
import { removeHover } from '../utils/validations'

const CreatePin = ({ isOpen, onClose, user_id }: any) => {
  const height = use100vh()
  const { isOpen: modalIsOpen, onOpen, onClose: modalOnClose } = useDisclosure()
  const [loading, setLoading] = useState(false)
  const [errorMessage, setError] = useState('')
  const inputRef = useRef(null)

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm()

  const onSubmit = async (values) => {
    setLoading(true)
    try {
      const { data } = await Axios.patch('/user/update-pin', { pin: values.pin, user_id })
      setLoading(false)
      onOpen()
    } catch (error: any) {
      setError(error?.response?.data?.message)
      onOpen()
      setLoading(false)
    }
  }

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent
        bg="#fcfcfc"
        h="-webkit-fill-available"
        maxW={{ md: '480px !important' }}
        mx="auto">
        <Flex
          pos="relative"
          flexDir="column"
          maxW={{ md: '480px' }}
          w="full"
          mx="auto"
          pb="2.4rem"
          px="2.4rem"
          pt="1.2rem"
          h={height || undefined}
          as="form"
          onSubmit={handleSubmit(onSubmit)}>
          <HStack mb="1rem" pb="2.4rem">
            <IconButton
              onClick={onClose}
              justifyContent="flex-start"
              color="transparent"
              bg="transparent"
              w="30px"
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              _focus={{ bg: 'transparent' }}
              aria-label="Search database"
              icon={
                <svg
                  width="15"
                  height="14"
                  viewBox="0 0 15 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3.4451 6.10003H14.3996V7.89997H3.4451L8.27256 12.7274L7 14L0 7L7 0L8.27256 1.27256L3.4451 6.10003Z"
                    fill="#2E279D"
                  />
                </svg>
              }
            />
            <Heading
              fontWeight="600"
              ml="-48px !important"
              flex="1"
              textAlign="center"
              fontSize="1.6rem"
              color="black">
              Payment Details
            </Heading>
          </HStack>
          <BaseHeading title="Set a Secure PIN" />
          <Text fontSize="1.2rem" mb="2.4rem">
            Create a 6-digit PIN to enable you login to Kappa
          </Text>
          <Text mb="1rem" color="black">
            Enter PIN
          </Text>

          <HStack>
            <Controller
              control={control}
              name="pin"
              rules={{
                required: { value: true, message: 'PIN is required' },
                maxLength: { value: 6, message: 'PIN must be 6 digits' },
                minLength: { value: 6, message: 'PIN must be 6 digits' }
              }}
              render={({ field: { onChange, value } }) => (
                <PinInput
                  isInvalid={errors.pin}
                  size="lg"
                  onChange={(val) => {
                    onChange(val)
                    if (val.length === 6) {
                      inputRef?.current?.focus()
                    }
                  }}
                  otp
                  mask
                  placeholder="">
                  {Array(6)
                    .fill('')
                    .map(() => (
                      <PinInputField color="black" />
                    ))}
                </PinInput>
              )}
            />
          </HStack>
          <ErrorMessage isError={errors.pin && errors.pin.message} />

          <Text mb="1rem" color="black" mt="2.4rem">
            Confirm PIN
          </Text>

          <HStack w="full">
            <Controller
              control={control}
              name="confirm_pin"
              rules={{
                required: { value: true, message: 'Confirm PIN is required' },
                maxLength: { value: 6, message: 'Confirm PIN must be 6 digits' },
                minLength: { value: 6, message: 'Confirm PIN must be 6 digits' },
                validate: (value) => value === watch('pin') || 'The PINs do not match'
              }}
              render={({ field: { onChange } }) => (
                <PinInput
                  isInvalid={errors.confirm_pin}
                  size="lg"
                  onChange={onChange}
                  otp
                  mask
                  placeholder="">
                  <PinInputField ref={inputRef} color="black" />
                  {Array(5)
                    .fill('')
                    .map(() => (
                      <PinInputField color="black" />
                    ))}
                </PinInput>
              )}
            />
          </HStack>
          <ErrorMessage isError={errors.confirm_pin && errors.confirm_pin.message} />

          <Text mt="2rem !important" fontSize="1.2rem">
            By proceeding with create account, you agree to Kappa{' '}
            <Link
              fontWeight="500"
              target="_blank"
              color="brand.blue"
              as={RouterLink}
              rel="noreferrer"
              to={ROUTES.settings.tAndC}
              {...removeHover}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              fontWeight="500"
              color="brand.blue"
              target="_blank"
              as={RouterLink}
              rel="noreferrer"
              to={ROUTES.settings.privacyPolicy}
              {...removeHover}>
              Privacy Policy
            </Link>
          </Text>
          <Button isLoading={loading} type="submit" mt="2.4rem" variant="blue">
            Create Account
          </Button>
        </Flex>
      </DrawerContent>

      <ActionModal
        error={!!errorMessage}
        message={errorMessage}
        isOpen={modalIsOpen}
        onClose={modalOnClose}
      />
    </Drawer>
  )
}

export { CreatePin }
